import { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Dropzone, { FileWithPath } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button, CustomIcon } from '~components/index';
import InputLabel from '../InputLabel/InputLabel';
import './inputDropzone.scss';

interface Props extends CommonInputProps {
  label?: string;
  extensions: FileExtensions[];
  placeholder?: string;
  isLoading?: boolean;
  multipleFiles?: boolean;
  hookOnChange?: (value: FileWithPath | FileWithPath[] | null) => void;
  onInputChange?: (value: FileWithPath | FileWithPath[] | null) => void;
  onDelete?: () => void;
  filePreSelected?: FileWithPath | null;
  defaultValue?: string;
}

const InputDropzone = ({
  name,
  label,
  labelHelper,
  extensions = [],
  disabled = false,
  required = false,
  design = 'col-12',
  hookError,
  isLoading = false,
  multipleFiles = false,
  defaultValue,
  filePreSelected = null,
  hookOnChange = () => {},
  onInputChange = () => {},
  onDelete = () => {}
}: Props) => {
  const { t } = useTranslation();
  const [fileSelected, setFileSelected] = useState<
    FileWithPath | FileWithPath[] | null
  >(filePreSelected);
  const acceptedExtensions = extensions
    .map((extension) => `.${extension}`)
    .join(', ');

  const fileSize = useMemo(() => {
    if (fileSelected && !Array.isArray(fileSelected)) {
      return Number((fileSelected.size / (1024 * 1024)).toFixed(2));
    } else return null;
  }, [fileSelected]);

  return (
    <div className={`customInput ${design}`}>
      <InputLabel
        helper={labelHelper}
        name={name}
        text={label}
        required={required}
      />
      <div className={disabled ? 'customInput_disabled' : ''}>
        <Dropzone
          disabled={disabled}
          accept={acceptedExtensions}
          onDrop={(files) => {
            if (multipleFiles) {
              setFileSelected(files);
              hookOnChange(files);
              onInputChange(files);
            } else {
              setFileSelected(files[0]);
              hookOnChange(files[0]);
              onInputChange(files[0]);
            }
          }}
          noDrag
        >
          {({ getRootProps, getInputProps }) => (
            <div
              className={`inputDropzone d-flex gap-2 ${
                disabled ? 'inputDropzone_disabled' : ''
              }`}
            >
              <div
                className="inputDropzone_trigger items_row_centered"
                {...getRootProps()}
              >
                {isLoading ? (
                  <Spinner
                    role="loading_spinner"
                    animation="border"
                    className="dropzone_spinner"
                  />
                ) : (
                  <CustomIcon name="fileArrowUp" size="MD" />
                )}
                <input {...getInputProps()} hidden />
              </div>
              <div className="d-flex flex-column justify-content-center">
                {!fileSelected ? (
                  <div>
                    {t('components.input_dropzone_extensions')}
                    <span className="ps-1 fw-bold">{acceptedExtensions}</span>
                  </div>
                ) : (
                  t('components.input_dropzone_file_loaded')
                )}
                {fileSelected && (
                  <div className="inputDropzone_thumb d-flex gap-2 align-items-center">
                    <p>
                      {fileSelected && !Array.isArray(fileSelected)
                        ? fileSelected?.path
                        : fileSelected
                            ?.map((file) => file.path)
                            .join(', ')}{' '}
                      - {fileSize}MB
                    </p>
                    <button
                      type="button"
                      disabled={disabled}
                      className="inputDropzone_thumb_btn"
                      onClick={() => {
                        setFileSelected(null);
                        hookOnChange(null);
                        onInputChange(null);
                        onDelete();
                      }}
                    >
                      X
                    </button>
                  </div>
                )}

                <div className="d-flex gap-1 align-items-center">
                  <Button
                    variant="link"
                    iconName="fileDownload"
                    fileToDownload={defaultValue}
                    text="btns.download_file"
                  />
                  <button
                    type="button"
                    disabled={disabled}
                    className="inputDropzone_thumb_btn"
                    onClick={() => {
                      setFileSelected(null);
                      hookOnChange(null);
                      onInputChange(null);
                      onDelete();
                      toast.success(t('toasts.file_deleted'));
                    }}
                  >
                    X
                  </button>
                </div>
              </div>
            </div>
          )}
        </Dropzone>
      </div>
      {hookError?.message && (
        <p className="customInput_error">{t(hookError?.message)}</p>
      )}
    </div>
  );
};

export default InputDropzone;
